import { mixins } from 'vue-class-component'
import { Component, Watch } from 'vue-property-decorator'
import { State } from 'vuex-class'

import { Breadcrumb } from '@/interfaces/breadcrumbs'
import { Case, PhaseCases } from '@/modules/Case/interfaces/case'
import { ApolloResponse } from '@/interfaces/apollo'
import { showErrors } from '@/utils/snack'
import { translateCaseTypeKey } from '@/utils/translate'
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue'
import AppSearch from '@/components/AppSearch.vue'
import AppRefresh from '@/components/AppRefresh.vue'
import AppTextField from '@/components/AppTextField.vue'
import AppTableDate from '@/components/AppTableDate.vue'
import AppSortButton from '@/components/AppSortButton.vue'
import AppSelector from '@/components/AppSelector.vue'
import CaseDashboardCard from '@/modules/Case/components/CaseDashboardCard.vue'
import CaseTypeSelector from '@/modules/Case/components/CaseTypeSelector.vue'
import { getCases, getNewCasesFromProducer } from '@/modules/Case/gql/case.gql'
import { caseType, caseTypeClass, casePriorityClass } from '@/modules/Case/helpers/case'
import { dispatchPushCases, dispatchUpdateCases } from '@/store/dispatches'

import CaseDialog from '@/modules/Case/scripts/case-dialog'

type Phase = 'QUOTING' | 'PROPOSAL' | 'BINDING' | 'COMPLETED'

@Component({
  components: {
    AppBreadcrumbs,
    AppSearch,
    AppRefresh,
    AppTextField,
    AppTableDate,
    CaseDashboardCard,
    AppSortButton,
    AppSelector,
    CaseTypeSelector
  }
})
export default class CaseDashboard extends mixins(CaseDialog) {
  breadcrumbs: Breadcrumb[] = [
    {
      text: 'Insurance Cases'
    },
    {
      text: 'Case'
    }
  ]

  searchVal = ''
  searchAllVal = ''

  caseTypeClass = caseTypeClass
  casePriorityClass = casePriorityClass

  caseType = caseType

  sortBy = 'ID'
  sortDesc = true

  isSimple = false

  myHotCases: Case[] = []
  allCases: Case[] = []
  fromProducer: Case[] = []

  translateCaseTypeKey = (key: string) => translateCaseTypeKey(key)

  tableLoading = false
  myCaseLoading = false

  quotingCasesCurrentPage = 1
  proposalCasesCurrentPage = 1
  bindingCasesCurrentPage = 1
  completedCasesCurrentPage = 1

  quotingCasesTotalCount = 0
  proposalCasesTotalCount = 0
  bindingCasesTotalCount = 0
  completedCasesTotalCount = 0

  perPageNum = 8

  order = {
    QUOTING: {
      orderBy: 'UPDATED_AT',
      asc: false
    },
    PROPOSAL: {
      orderBy: 'UPDATED_AT',
      asc: false
    },
    BINDING: {
      orderBy: 'UPDATED_AT',
      asc: false
    },
    COMPLETED: {
      orderBy: 'UPDATED_AT',
      asc: false
    }
  }

  isTop = true

  @State(state => state.theCase.quoting) quotingCases!: Case[]
  @State(state => state.theCase.proposal) proposalCases!: Case[]
  @State(state => state.theCase.binding) bindingCases!: Case[]
  @State(state => state.theCase.completed) completedCases!: Case[]

  @Watch('isSimple')
  onIsSimpleChanged (val: boolean) {
    this.perPageNum = val ? 12 : 8
  }

  mounted () {
    this.getTopCases()
  }

  quotingCaseScrollHandler () {
    const element = this.$refs.quotingCasesBox as HTMLElement
    var clientHeight = element.clientHeight
    var scrollTop = element.scrollTop
    var scrollHeight = element.scrollHeight
    if ((scrollTop + clientHeight === scrollHeight) && (this.quotingCasesCurrentPage * this.perPageNum < this.quotingCasesTotalCount)) {
      this.quotingCasesCurrentPage += 1
      this.getCases('QUOTING')
    }
  }

  proposalCaseScrollHandler () {
    const element = this.$refs.proposalCasesBox as HTMLElement
    var clientHeight = element.clientHeight
    var scrollTop = element.scrollTop
    var scrollHeight = element.scrollHeight
    if ((scrollTop + clientHeight === scrollHeight) && (this.proposalCasesCurrentPage * this.perPageNum < this.proposalCasesTotalCount)) {
      this.proposalCasesCurrentPage += 1
      this.getCases('PROPOSAL')
    }
  }

  bindingCaseScrollHandler () {
    const element = this.$refs.bindingCasesBox as HTMLElement
    var clientHeight = element.clientHeight
    var scrollTop = element.scrollTop
    var scrollHeight = element.scrollHeight
    if ((scrollTop + clientHeight === scrollHeight) && (this.bindingCasesCurrentPage * this.perPageNum < this.bindingCasesTotalCount)) {
      this.bindingCasesCurrentPage += 1
      this.getCases('BINDING')
    }
  }

  completedCaseScrollHandler () {
    const element = this.$refs.completedCasesBox as HTMLElement
    var clientHeight = element.clientHeight
    var scrollTop = element.scrollTop
    var scrollHeight = element.scrollHeight
    if ((scrollTop + clientHeight === scrollHeight) && (this.completedCasesCurrentPage * this.perPageNum < this.completedCasesTotalCount)) {
      this.completedCasesCurrentPage += 1
      this.getCases('COMPLETED')
    }
  }

  getTopCases () {
    this.clearData()
    this.getCases('QUOTING', true)
    this.getCases('PROPOSAL', true)
    this.getCases('BINDING', true)
    this.getCases('COMPLETED', true)
    this.getCasesFromProducer()
  }

  clearData () {
    this.quotingCasesCurrentPage = 1
    this.proposalCasesCurrentPage = 1
    this.bindingCasesCurrentPage = 1
    this.completedCasesCurrentPage = 1

    this.quotingCasesTotalCount = 0
    this.proposalCasesTotalCount = 0
    this.bindingCasesTotalCount = 0
    this.completedCasesTotalCount = 0
  }

  routeToDetail (item: any) {
    return {
      name: 'CaseDetailDetail',
      params: {
        id: item.id
      }
    }
  }

  routeToAccountDetail (item: any) {
    if (item.account) {
      return {
        name: 'AccountDetailDetail',
        params: {
          id: item.account.id
        }
      }
    }
  }

  routeToContactDetail (item: any) {
    if (item.primaryContact) {
      return {
        name: 'ContactDetailDetail',
        params: {
          id: item.primaryContact.id
        }
      }
    }
  }

  routeToProducerDetail (item: any) {
    if (item.producer) {
      return {
        name: 'ProducerDetailDetail',
        params: {
          id: item.producer.id
        }
      }
    }
  }

  routeTo (name: string, params: any) {
    this.$router.push({
      name,
      params
    })
  }

  quotingSortByPriority (type: string) {
    this.sortCases('QUOTING', type)
  }

  proposalSortByPriority (type: string) {
    this.sortCases('PROPOSAL', type)
  }

  bindingSortByPriority (type: string) {
    this.sortCases('BINDING', type)
  }

  completedSortByPriority (type: string) {
    this.sortCases('COMPLETED', type)
  }

  fromProducerSort (type: string) {
    this.getCasesFromProducer(type === 'asc')
  }

  caseTypeChangeHandler (index: number) {
    this.isTop = index === 0
    this.getTopCases()
  }

  async getCases (phase: Phase, isInit?: boolean) {
    this.myCaseLoading = true
    const { quotingCasesCurrentPage, proposalCasesCurrentPage, bindingCasesCurrentPage, completedCasesCurrentPage } = this
    let offset = 0
    switch (phase) {
      case 'QUOTING':
        offset = (quotingCasesCurrentPage - 1) * this.perPageNum
        break
      case 'PROPOSAL':
        offset = (proposalCasesCurrentPage - 1) * this.perPageNum
        break
      case 'BINDING':
        offset = (bindingCasesCurrentPage - 1) * this.perPageNum
        break
      case 'COMPLETED':
        offset = (completedCasesCurrentPage - 1) * this.perPageNum
        break
      default:
        break
    }
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCases,
        variables: {
          filter: {
            inquiryName: this.searchVal,
            phase
          },
          isTop: this.isTop,
          isOpen: true,
          order: this.order[phase],
          paging: {
            offset,
            limit: this.perPageNum
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.myCaseLoading = false
      let type = ''
      switch (phase) {
        case 'QUOTING':
          type = 'quoting'
          this.quotingCasesTotalCount = result.data.getCases.paging.totalCount
          break
        case 'PROPOSAL':
          type = 'proposal'
          this.proposalCasesTotalCount = result.data.getCases.paging.totalCount
          break
        case 'BINDING':
          type = 'binding'
          this.bindingCasesTotalCount = result.data.getCases.paging.totalCount
          break
        case 'COMPLETED':
          type = 'completed'
          this.completedCasesTotalCount = result.data.getCases.paging.totalCount
          break
        default:
          break
      }
      const phaseCases: PhaseCases = {
        type,
        cases: result.data.getCases.cases
      }
      if (isInit) {
        dispatchUpdateCases(phaseCases)
      } else {
        dispatchPushCases(phaseCases)
      }
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async getCasesFromProducer (asc = false) {
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getNewCasesFromProducer,
        variables: {
          filter: {
            inquiryName: this.searchVal,
            ifNewProducer: true
          },
          order: {
            orderBy: 'CREATED_AT',
            asc
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      const res = result.data.getNewCasesFromProducer
      this.fromProducer = res
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }

  async sortCases (phase: Phase, asc: string) {
    this.myCaseLoading = true
    let offset = 0
    let type = ''
    let limit = this.perPageNum
    switch (phase) {
      case 'QUOTING':
        type = 'quoting'
        limit = this.quotingCases.length
        break
      case 'PROPOSAL':
        type = 'proposal'
        limit = this.proposalCases.length
        break
      case 'BINDING':
        type = 'binding'
        limit = this.bindingCases.length
        break
      case 'COMPLETED':
        type = 'completed'
        limit = this.completedCases.length
        break
      default:
        break
    }
    this.order[phase] = asc === ''
      ? {
        orderBy: 'UPDATED_AT',
        asc: false
      }
      : asc === 'asc'
        ? {
          orderBy: 'CASE_PRIORITY',
          asc: true
        }
        : {
          orderBy: 'CASE_PRIORITY',
          asc: false
        }
    try {
      const result: ApolloResponse = await this.$apollo.query({
        query: getCases,
        variables: {
          filter: {
            inquiryName: this.searchVal,
            phase
          },
          order: this.order[phase],
          isTop: this.isTop,
          isOpen: true,
          paging: {
            offset,
            limit
          }
        }
      })
      if (!result.data) {
        showErrors(result.errors)
        return
      }
      this.myCaseLoading = false

      const phaseCases: PhaseCases = {
        type,
        cases: result.data.getCases.cases
      }
      dispatchUpdateCases(phaseCases)
    } catch (e) {
      showErrors(e.graphQLErrors)
    }
  }
}
